/**
 * One-time payment data-access layer for the payment modal.
 *
 * Two operations: the org-wide maximum a member may schedule in one payment, and the schedule
 * itself. The maximum is a plain read with no Failure variant, so a misconfigured Salesforce
 * metadata record arrives as a GraphQL error rather than a domain code.
 */

import { gqlRequest } from '@mdvip/shared/lib/graphql-client';
import {
  MaximumPaymentAmountDocument,
  ScheduleOneTimePaymentDocument,
  type ScheduleOneTimePaymentFailureCode,
} from '@/graphql/generated/graphql';

export interface MaximumPaymentAmount {
  amount: number;
  /** Salesforce-authored explanation of the configured cap. Not shown today; kept for support. */
  description: string;
}

export interface ScheduleOneTimePaymentRequest {
  amount: number;
  /** The selected saved account's `Payment_Profile__c` id, the same id set-default takes. */
  paymentProfileId: string;
  /** ISO `yyyy-MM-dd`, which the API requires to be strictly after today in America/New_York. */
  scheduleDate: string;
}

export interface ScheduledPayment {
  id: string;
  name: string;
}

/**
 * Thrown when scheduleOneTimePayment returns its Failure variant. `NOT_FOUND` deliberately covers
 * an unknown, someone else's, or a deactivated payment profile, so the response leaks no
 * enumeration oracle.
 */
export class ScheduleOneTimePaymentError extends Error {
  constructor(public readonly code: ScheduleOneTimePaymentFailureCode) {
    super(code);
    this.name = 'ScheduleOneTimePaymentError';
  }
}

export async function requestMaximumPaymentAmount(): Promise<MaximumPaymentAmount> {
  const data = await gqlRequest(MaximumPaymentAmountDocument);
  return data.maximumPaymentAmount;
}

export async function requestScheduleOneTimePayment(input: ScheduleOneTimePaymentRequest): Promise<ScheduledPayment> {
  const data = await gqlRequest(ScheduleOneTimePaymentDocument, { input });
  const result = data.scheduleOneTimePayment;
  if (result.__typename === 'ScheduleOneTimePaymentFailure') {
    throw new ScheduleOneTimePaymentError(result.code);
  }
  return { id: result.id, name: result.name };
}
